import React from 'react';
import { useNotifications } from '@doveit/bricks';
import { Task } from '../../../providers/api/dtos';
import TaskForm, { TaskFormModel, TaskFormProps } from '../../components/task-form/TaskForm';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { createTask, updateTask } from '../../../providers/api/task/taskProvider';
import { TaskStatus } from '../../../domain/types';
import { getAssigneeOptions } from '../../constants';

export interface UpsertTaskProps {
  task?: Partial<Task>,
  formRef?: TaskFormProps['innerRef'],
  onSuccess?: (task: Task) => void,
}

export const UPSERT_TASK_ERROR_MESSAGE = 'Non è stato possibile effettuare l\'operazione';
export const UPSERT_TASK_SUCCESS_MESSAGE = 'Operazione effettuata con successo';

const UpsertTask: React.FC<UpsertTaskProps> = ({
  task,
  formRef,
  onSuccess,
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const { addSuccess, addError } = useNotifications();
  const {
    userIsAdmin,
    userIsContentEditor,
    user: defaultReporter,
    mainUserRole,
  } = useRBAC();

  const initialValues = React.useMemo(() => Object.fromEntries(
    Object.entries({
      type: task?.type,
      title: task?.title,
      description: task?.description,
      priority: task?.priority,
      assignee: task?.assignee,
    }).filter(([_, value]) => value != null), // Rimuove chiavi con null o undefined
  ), [task]);

  const handleSubmit = React.useCallback(async ({
    type,
    priority,
    title,
    description,
    assignee,
  }: TaskFormModel) => {
    setIsSaving(true);

    try {
      const payload: Task = {
        ...(task || {}),
        status: task?.status ?? TaskStatus.TODO,
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        reporter: task?.reporter ?? defaultReporter.email,
        assignee: assignee || undefined,
      };

      const result = await (task?.id
        ? updateTask(task.id, payload)
        : createTask(payload)
      );

      setIsSaving(false);
      addSuccess(UPSERT_TASK_SUCCESS_MESSAGE);
      onSuccess?.(result);
    } catch (_) {
      setIsSaving(false);
      addError(UPSERT_TASK_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, defaultReporter.email, onSuccess, task]);

  return (
    <TaskForm
      innerRef={formRef}
      initialValues={initialValues}
      loading={isSaving}
      onSubmit={handleSubmit}
      showAssignee={userIsAdmin || userIsContentEditor}
      assignees={getAssigneeOptions(mainUserRole)}
    />
  );
};

export default UpsertTask;
